import { motion, type MotionValue, useReducedMotion } from 'motion/react';
import React from 'react';

import { useEventCallback } from '@gitbook/util-react-hooks';

import { type OrganizationAgentLocation } from '../Agent';
import { AgentDockConversation, type AgentDockConversationControls } from './AgentDockConversation';
import {
    AGENT_DOCK_WINDOW_MAX_HEIGHT,
    AGENT_DOCK_WINDOW_MIN_HEIGHT,
    clampAgentDockWindowHeight,
    useAgentDockWindowHeight,
} from './layout';
import { type AgentDockTab, getAgentDockTabId } from './state';

/**
 * Width of the window, matching its `w-96`. Exported so the dock can keep the window inside its
 * row when aligning it with a tab near the left edge.
 */
export const AGENT_DOCK_WINDOW_WIDTH = 384;

/**
 * How far an arrow key moves the window's top edge.
 */
const KEYBOARD_RESIZE_STEP = 16;

/**
 * The conversation window sitting above the dock's tab bar.
 *
 * Squarer than the old floating panel on purpose: it reads as a window attached to its tab rather
 * than a bubble floating over the page — which is also why it aligns with its tab and scales in
 * from it. The conversation inside it is the same one the anchored panel shows; what the window
 * adds is a place to put it away to, a way to move it out to the side, and a top edge to drag it
 * taller or shorter by.
 *
 * One element for every conversation, remounting only its content: the surface has to survive a
 * tab switch for its position to animate from one tab to the other.
 */
export function AgentDockWindow(
    props: {
        tab: AgentDockTab;
        title: string;
        /** Where the user is, so the agent can resolve "this site" without going looking. */
        location: OrganizationAgentLocation;
        /**
         * Horizontal position, aligning the window with the active tab. Owned and driven by the
         * dock, which measures the tabs and decides whether a change slides or snaps; the window
         * only renders it.
         */
        x: MotionValue<number>;
        /**
         * The space above the window's bottom edge, up to the top of the column it lives in. Owned
         * by the dock like `x`: the column clips whatever sticks out of it, and only the dock can
         * see where it ends.
         */
        room: number;
    } & AgentDockConversationControls
) {
    const { tab, title, location, x, room, ...controls } = props;
    const prefersReducedMotion = useReducedMotion();
    const [storedHeight, setStoredHeight] = useAgentDockWindowHeight();
    // The height mid-drag, remembered only on release: the preference is written — and shared with
    // the user's other browser windows — once per drag rather than once per pointer move.
    const [draggedHeight, setDraggedHeight] = React.useState<number | null>(null);
    // Clamped as it renders, not only as it is dragged. A height dragged out on a tall screen has
    // to fit a shorter one too, or the column would clip the header away, controls and all.
    const height = clampAgentDockWindowHeight(draggedHeight ?? storedHeight, room);

    // Remounts the conversation when the window moves to another one, scoping every piece of chat
    // state to its conversation while the surface itself persists.
    const contentKey = getAgentDockTabId(tab);

    return (
        <motion.div
            // Same surface treatment as a Spine popover — which is what the panel this replaces
            // was — so the window sits on the page like every other floating surface. The border
            // is dark-mode only there: in light mode the shadow alone separates it. Except along
            // the top, which is the resize handle's edge: lit while the handle is hovered or held,
            // and kept as a transparent line in light mode so that lighting it moves nothing.
            // Squarer than the old panel's `3xl` on purpose: it reads as a window on its tab, not a
            // bubble. The transform origin is the corner touching the tab, so the scale grows out
            // of it.
            className="absolute right-0 bottom-full z-overlay mb-1 flex w-96 origin-bottom-right flex-col gap-2 overflow-hidden rounded-[1.25rem] border-t border-transparent bg-base shadow-overlay transition-colors has-[[data-resize-handle]:focus-visible]:border-t-neutral-focus has-[[data-resize-handle]:hover]:border-t-neutral-focus data-[resizing=true]:border-t-neutral-focus dark:border dark:border-neutral-subtle"
            style={{ x, height }}
            data-resizing={draggedHeight !== null}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{
                opacity: 0,
                scale: 0.96,
                transition: { duration: prefersReducedMotion ? 0 : 0.1, ease: 'easeIn' },
            }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.12, ease: 'easeOut' }}
        >
            <AgentDockWindowResizeHandle
                height={height}
                room={room}
                onResize={setDraggedHeight}
                onResizeEnd={(next) => {
                    setDraggedHeight(null);
                    setStoredHeight(next);
                }}
            />
            <AgentDockConversation
                key={contentKey}
                tab={tab}
                title={title}
                location={location}
                {...controls}
            />
        </motion.div>
    );
}

/**
 * The window's top edge, as a drag target: pulling it up makes the window taller.
 *
 * Nothing to look at by itself. The window lights its top border while this is hovered or held —
 * see its class list — which is what says the edge can be taken hold of.
 */
function AgentDockWindowResizeHandle(props: {
    height: number;
    room: number;
    /** Called on every move of a drag, for the live height. */
    onResize: (height: number) => void;
    /** Called once a drag or a keypress settles on a height worth keeping. */
    onResizeEnd: (height: number) => void;
}) {
    const { height, room, onResize, onResizeEnd } = props;
    const handleRef = React.useRef<HTMLDivElement>(null);
    const [isDragging, setDragging] = React.useState(false);
    // Where the drag took hold. The pointer's travel is added to the height it started from rather
    // than the height read off where the pointer is, so the edge stays where it was grabbed instead
    // of jumping under the pointer.
    const dragStartRef = React.useRef<{ clientY: number; height: number } | null>(null);

    const heightFromPointer = useEventCallback((clientY: number) => {
        const start = dragStartRef.current;
        if (!start) {
            return height;
        }
        // Up is taller: the window grows away from the bar it sits on.
        return clampAgentDockWindowHeight(start.height + (start.clientY - clientY), room);
    });

    const handlePointerDown = useEventCallback((event: React.PointerEvent<HTMLDivElement>) => {
        if (event.button !== 0) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        dragStartRef.current = { clientY: event.clientY, height };
        handleRef.current?.setPointerCapture(event.pointerId);
        setDragging(true);
    });

    const handlePointerMove = useEventCallback((event: React.PointerEvent<HTMLDivElement>) => {
        if (!isDragging) {
            return;
        }
        const next = heightFromPointer(event.clientY);
        if (next !== height) {
            onResize(next);
        }
    });

    const handlePointerUp = useEventCallback((event: React.PointerEvent<HTMLDivElement>) => {
        if (!isDragging) {
            return;
        }
        handleRef.current?.releasePointerCapture(event.pointerId);
        const next = heightFromPointer(event.clientY);
        dragStartRef.current = null;
        setDragging(false);
        onResizeEnd(next);
    });

    const handleKeyDown = useEventCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
        const step =
            event.key === 'ArrowUp'
                ? KEYBOARD_RESIZE_STEP
                : event.key === 'ArrowDown'
                  ? -KEYBOARD_RESIZE_STEP
                  : 0;
        if (step === 0) {
            return;
        }
        event.preventDefault();
        onResizeEnd(clampAgentDockWindowHeight(height + step, room));
    });

    // Holds the resize cursor and suppresses selection everywhere, so the drag survives the pointer
    // leaving the handle.
    React.useEffect(() => {
        if (!isDragging) {
            return;
        }
        document.body.classList.add('resizing-vertical');
        return () => {
            document.body.classList.remove('resizing-vertical');
        };
    }, [isDragging]);

    return (
        <div
            ref={handleRef}
            data-resize-handle
            tabIndex={0}
            role="separator"
            aria-label="Resize window"
            aria-orientation="horizontal"
            aria-valuemin={AGENT_DOCK_WINDOW_MIN_HEIGHT}
            aria-valuemax={clampAgentDockWindowHeight(AGENT_DOCK_WINDOW_MAX_HEIGHT, room)}
            aria-valuenow={Math.round(height)}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onKeyDown={handleKeyDown}
            // Lies over the header's top padding, where there is nothing to press. Panning is
            // turned off because the dock is what a touch device gets: without it the browser
            // claims a vertical drag as a scroll and cancels the pointer mid-resize, which
            // preventDefault on the pointerdown does not stop.
            className="absolute inset-x-0 top-0 z-10 h-2 cursor-row-resize touch-none focus-visible:outline-hidden"
        />
    );
}
